import type { FC } from "react";
import {
	Alert,
	Badge,
	Col,
	OverlayTrigger,
	Row,
	Spinner,
	Table,
	Tooltip,
} from "react-bootstrap";
import {
	type AIVisibilityResult,
	LLM_PLATFORMS,
	type LLMPlatformType,
	type SeoPromptCategory,
} from "shared";
import { useCompany } from "../../company/useCompany";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { StatusPlaceholder } from "../../components/StatusPlaceholder";
import { Svg } from "../../svg";
import { useSeoConfig } from "../useSeoConfig";
import { SovTrend } from "./SovTrend";
import {
	useAIVisibility,
	useAIVisibilityActions,
	useSeoMetrics,
} from "./useAIVisibility";

type Props = {
	onGoToSettings: () => void;
};

const PLATFORM_LABELS: Record<LLMPlatformType, string> = {
	chatgpt: "ChatGPT",
	perplexity: "Perplexity",
	gemini: "Gemini",
	claude: "Claude",
};

const CATEGORY_ORDER: SeoPromptCategory[] = [
	"brand",
	"category",
	"local",
	"competitor",
];

const CATEGORY_LABEL: Record<SeoPromptCategory, string> = {
	brand: "Brand",
	category: "Category",
	local: "Local",
	competitor: "Competitor",
};

const numericStyle = { fontVariantNumeric: "tabular-nums" } as const;

const formatPct = (value: number | null) =>
	value === null ? "n/a" : `${Math.round(value)}%`;

const engineLabel = (engine: string) =>
	PLATFORM_LABELS[engine as LLMPlatformType] ?? engine;

type Coverage = { cited: number; answered: number };

const engineCoverage = (
	matrix: AIVisibilityResult["matrix"],
	engine: string,
): Coverage =>
	matrix.reduce<Coverage>(
		(acc, entry) => {
			const cell = entry.results[engine];
			if (!cell) {
				return acc;
			}

			return {
				cited: acc.cited + (cell.cited ? 1 : 0),
				answered: acc.answered + 1,
			};
		},
		{ cited: 0, answered: 0 },
	);

const blendedCoverage = (matrix: AIVisibilityResult["matrix"]): Coverage =>
	matrix.reduce<Coverage>(
		(acc, entry) => {
			for (const cell of Object.values(entry.results)) {
				if (!cell) {
					continue;
				}

				acc.cited += cell.cited ? 1 : 0;
				acc.answered += 1;
			}

			return acc;
		},
		{ cited: 0, answered: 0 },
	);

const heroSubtext = (value: number | null, coverage: Coverage) =>
	value === null
		? "No data this run"
		: `Cited in ${coverage.cited} of ${coverage.answered} prompts`;

const RunningState: FC = () => (
	<Card>
		<Card.Body className="d-flex flex-column align-items-center gap-3 py-5">
			<Spinner animation="border" />
			<p className="text-muted mb-0">
				Checking AI answers across ChatGPT, Perplexity, Gemini, and Claude —
				this takes a few minutes.
			</p>
		</Card.Body>
	</Card>
);

type FailedStateProps = {
	errorMessage: string | null;
	onRetry: () => void;
	retrying: boolean;
};

const FailedState: FC<Readonly<FailedStateProps>> = ({
	errorMessage,
	onRetry,
	retrying,
}) => (
	<Alert variant="danger">
		<Alert.Heading>AI visibility check failed</Alert.Heading>
		<p>{errorMessage || "Something went wrong. Please try again."}</p>
		<Button
			variant="danger"
			loading={retrying}
			onClick={onRetry}
			icon="refresh"
		>
			Run again
		</Button>
	</Alert>
);

type EmptyStateProps = {
	onRun: () => void;
	pending: boolean;
};

const EmptyState: FC<Readonly<EmptyStateProps>> = ({ onRun, pending }) => (
	<StatusPlaceholder
		label="Run your first AI visibility check"
		sublabel="We'll ask ChatGPT, Perplexity, Gemini, and Claude the prompts a customer might ask, and measure how often your brand shows up compared to competitors."
		image={Svg.Data}
	>
		<Button onClick={onRun} loading={pending} size="sm" icon="bolt">
			Run AI visibility check
		</Button>
	</StatusPlaceholder>
);

type TrendSectionProps = {
	companyName: string;
};

const TrendSection: FC<Readonly<TrendSectionProps>> = ({ companyName }) => {
	const metrics = useSeoMetrics(6);
	const blended = metrics.series.ai_sov_blended ?? [];
	const nonNullCount = blended.filter((value) => value !== null).length;

	let topCompetitorKey: string | null = null;
	let topCompetitorLatest = Number.NEGATIVE_INFINITY;
	for (const [key, values] of Object.entries(metrics.series)) {
		if (!key.startsWith("competitor_sov:")) {
			continue;
		}

		const latest = [...values].reverse().find((value) => value !== null);
		if (
			latest !== undefined &&
			latest !== null &&
			latest > topCompetitorLatest
		) {
			topCompetitorLatest = latest;
			topCompetitorKey = key;
		}
	}

	if (nonNullCount < 2) {
		return (
			<p className="text-muted mb-0">
				Trends appear after your second monthly run.
			</p>
		);
	}

	const series = [{ label: companyName || "You", values: blended }];
	if (topCompetitorKey) {
		series.push({
			label: topCompetitorKey.slice("competitor_sov:".length),
			values: metrics.series[topCompetitorKey],
		});
	}

	return <SovTrend months={metrics.months} series={series} />;
};

type ResultViewProps = {
	result: AIVisibilityResult;
	onRunAgain: () => void;
	running: boolean;
};

const ResultView: FC<Readonly<ResultViewProps>> = ({
	result,
	onRunAgain,
	running,
}) => {
	const company = useCompany();
	const {
		coverage,
		sov,
		competitors,
		matrix,
		citedSources,
		month,
		baseline,
		summary,
	} = result;

	const heroTiles = [
		{
			label: "Blended SoV",
			value: formatPct(sov.blended),
			subtext: heroSubtext(sov.blended, blendedCoverage(matrix)),
		},
		{
			label: "Listing Health",
			value:
				result.listingHealthScore == null
					? "—"
					: String(Math.round(result.listingHealthScore)),
			subtext:
				result.listingHealthScore == null
					? "Not scored for this run"
					: "0-100 profile + reviews health",
		},
		...LLM_PLATFORMS.map((engine) => {
			const value = sov.byEngine[engine] ?? null;
			return {
				label: PLATFORM_LABELS[engine],
				value: formatPct(value),
				subtext: heroSubtext(value, engineCoverage(matrix, engine)),
			};
		}),
	];

	const failedEngines = coverage.enginesFailed.map(engineLabel);
	// One shared reason renders inline on the summary line; mixed reasons get
	// a per-engine breakdown so nothing is hidden.
	const failureReasons = coverage.failureReasons ?? {};
	const distinctReasons = Array.from(
		new Set(coverage.enginesFailed.map((e) => failureReasons[e] ?? "")),
	).filter(Boolean);
	const sharedReason = distinctReasons.length === 1 ? distinctReasons[0] : null;

	const matrixByCategory = CATEGORY_ORDER.map((category) => ({
		category,
		entries: matrix.filter((entry) => entry.category === category),
	})).filter((group) => group.entries.length > 0);

	return (
		<div className="ai-visibility-results">
			<div className="tab-header">
				<p className="tab-subtitle mb-0">
					{coverage.promptsChecked} prompts × {coverage.engines.length} engines
					· run {month}
				</p>
				<Button
					variant="ghost"
					size="sm"
					icon="refresh"
					loading={running}
					onClick={onRunAgain}
				>
					Run again
				</Button>
			</div>

			{/* The AI summary supersedes the baseline alert; runs without one (stored before this feature, or whose generation failed) fall back to the original text. */}
			{summary ? (
				<Alert variant="info" className="ai-visibility-summary">
					{summary}
				</Alert>
			) : (
				baseline && (
					<Alert variant="info">
						This is your baseline month. Zero citations is a normal starting
						point for a local brand — the goal is the trend from here.
					</Alert>
				)
			)}

			<Row className="g-3 mb-2">
				{heroTiles.map((tile) => (
					<Col xs={6} md={4} key={tile.label}>
						<Card className="text-center">
							<Card.Body>
								<div className="text-muted small">{tile.label}</div>
								<div className="fs-3 fw-semibold" style={numericStyle}>
									{tile.value}
								</div>
								<div className="text-muted small">{tile.subtext}</div>
							</Card.Body>
						</Card>
					</Col>
				))}
			</Row>

			{failedEngines.length > 0 && (
				<div className="text-warning small mb-1">
					<p className="mb-0">
						{failedEngines.join(", ")} returned no data this run
						{sharedReason ? ` — ${sharedReason}` : "."}
					</p>
					{!sharedReason && distinctReasons.length > 0 && (
						<ul className="mb-0">
							{coverage.enginesFailed
								.filter((engine) => failureReasons[engine])
								.map((engine) => (
									<li key={engine}>
										{engineLabel(engine)}: {failureReasons[engine]}
									</li>
								))}
						</ul>
					)}
				</div>
			)}
			<p className="text-muted small mb-4">
				AI answers are sampled and the index lags up to ~7 days; results show
				share of sampled answers, not absolute rankings.
			</p>

			<section className="mb-4">
				<h5>You vs competitors</h5>
				<Table responsive size="sm" bordered>
					<thead>
						<tr>
							<th>Brand</th>
							<th>Blended</th>
							{LLM_PLATFORMS.map((engine) => (
								<th key={engine}>{PLATFORM_LABELS[engine]}</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr className="table-primary fw-semibold">
							<td>{company.name}</td>
							<td style={numericStyle}>{formatPct(sov.blended)}</td>
							{LLM_PLATFORMS.map((engine) => (
								<td key={engine} style={numericStyle}>
									{formatPct(sov.byEngine[engine] ?? null)}
								</td>
							))}
						</tr>
						{competitors.map((competitor) => (
							<tr key={competitor.domain}>
								<td>{competitor.name}</td>
								<td style={numericStyle}>{formatPct(competitor.sovBlended)}</td>
								{LLM_PLATFORMS.map((engine) => (
									<td key={engine} style={numericStyle}>
										{formatPct(competitor.byEngine[engine] ?? null)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</Table>
			</section>

			<section className="mb-4">
				<h5>Prompt × engine matrix</h5>
				{matrixByCategory.map((group) => (
					<div key={group.category} className="mb-3">
						<h6 className="text-muted text-uppercase small">
							{CATEGORY_LABEL[group.category]}
						</h6>
						<Table responsive size="sm" bordered>
							<thead>
								<tr>
									<th>Prompt</th>
									{LLM_PLATFORMS.map((engine) => (
										<th key={engine}>{PLATFORM_LABELS[engine]}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{group.entries.map((entry) => (
									<tr key={entry.prompt}>
										<td>{entry.prompt}</td>
										{LLM_PLATFORMS.map((engine) => {
											const cell = entry.results[engine] ?? null;

											if (!cell) {
												return (
													<td key={engine}>
														<span className="text-muted fst-italic">
															No data
														</span>
													</td>
												);
											}

											let bg: string;
											let label: string;
											if (cell.cited) {
												bg = "success";
												label = "Cited";
											} else if (cell.mentioned) {
												bg = "warning";
												label = "Mentioned";
											} else {
												bg = "secondary";
												label = "Not found";
											}

											const badge = <Badge bg={bg}>{label}</Badge>;

											return (
												<td key={engine}>
													{cell.context ? (
														<OverlayTrigger
															placement="top"
															overlay={
																<Tooltip
																	id={`tooltip-${group.category}-${entry.prompt}-${engine}`}
																>
																	{cell.context}
																</Tooltip>
															}
														>
															<span>{badge}</span>
														</OverlayTrigger>
													) : (
														badge
													)}
												</td>
											);
										})}
									</tr>
								))}
							</tbody>
						</Table>
					</div>
				))}
			</section>

			<section className="mb-4">
				<h5>Cited sources</h5>
				{citedSources.length === 0 ? (
					<p className="text-muted">No sources have been cited yet.</p>
				) : (
					<ul className="list-unstyled mb-0">
						{citedSources.map((source) => (
							<li key={source.url}>
								{source.url} — cited {source.count}×
							</li>
						))}
					</ul>
				)}
			</section>

			<section>
				<h5>Trend</h5>
				<TrendSection companyName={company.name} />
			</section>
		</div>
	);
};

export const AIVisibilityTab: FC<Readonly<Props>> = ({ onGoToSettings }) => {
	const config = useSeoConfig();

	const run = useAIVisibility();
	const { run: runVisibility } = useAIVisibilityActions();

	// Three different blockers used to share one message, which told people to
	// redo a step they had already completed. Say which one actually applies.
	const setupBlocker = !config
		? {
				label: "Set up your SEO profile first",
				sublabel:
					"AI Visibility uses your competitors and AI prompts. Head to the Settings tab to generate your profile.",
			}
		: config.locationCode === null
			? {
					label: "Your SEO profile needs a location",
					sublabel:
						"AI Visibility runs against a specific market. In the Settings tab, search for your city or region in the Location field, pick a result, then save.",
				}
			: !config.reviewedAt
				? {
						label: "Save your SEO profile to finish setup",
						sublabel:
							"Your profile has been generated but not saved yet. Review it in the Settings tab, then hit Save.",
					}
				: null;

	if (setupBlocker) {
		return (
			<StatusPlaceholder
				label={setupBlocker.label}
				sublabel={setupBlocker.sublabel}
				image={Svg.Data}
			>
				<Button onClick={onGoToSettings} size="sm" icon="arrow_forward">
					Go to Settings
				</Button>
			</StatusPlaceholder>
		);
	}

	const isRunning =
		runVisibility.isPending ||
		run?.status === "pending" ||
		run?.status === "processing";

	if (isRunning) {
		return <RunningState />;
	}

	if (run?.status === "failed") {
		return (
			<FailedState
				errorMessage={run.errorMessage}
				onRetry={() => runVisibility.mutate()}
				retrying={runVisibility.isPending}
			/>
		);
	}

	if (!run?.result) {
		return (
			<EmptyState
				onRun={() => runVisibility.mutate()}
				pending={runVisibility.isPending}
			/>
		);
	}

	return (
		<ResultView
			result={run.result}
			onRunAgain={() => runVisibility.mutate()}
			running={runVisibility.isPending}
		/>
	);
};
